import { useState, useEffect, useCallback } from 'react';

export interface PwaDiagnosticCheck {
  name: string;
  status: 'loading' | 'ok' | 'fail' | 'warn' | 'info';
  message: string;
  detail?: string;
}

export interface PwaDiagnosticsState {
  checks: PwaDiagnosticCheck[];
  isLoading: boolean;
  overallStatus: 'loading' | 'installable' | 'not-installable' | 'installed' | 'unknown';
  summary: string;
}

const initialChecks: PwaDiagnosticCheck[] = [
  { name: 'HTTPS / Secure Context', status: 'loading', message: 'Checking...' },
  { name: 'Manifest Fetchable', status: 'loading', message: 'Checking...' },
  { name: 'Manifest Valid', status: 'loading', message: 'Checking...' },
  { name: 'Manifest Icons', status: 'loading', message: 'Checking...' },
  { name: 'Service Worker Registered', status: 'loading', message: 'Checking...' },
  { name: 'Service Worker Active', status: 'loading', message: 'Checking...' },
  { name: 'beforeinstallprompt Event', status: 'loading', message: 'Waiting for event...' },
  { name: 'Display Mode', status: 'loading', message: 'Checking...' },
];

function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext === true;
}

async function fetchManifest(): Promise<{ manifest: any; url: string } | null> {
  const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) return null;
  const url = new URL(link.href, window.location.origin).href;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const manifest = await res.json();
    return { manifest, url };
  } catch {
    return null;
  }
}

async function checkIconAccessible(src: string): Promise<boolean> {
  try {
    const url = new URL(src, window.location.origin).href;
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

export function usePwaDiagnostics() {
  const [state, setState] = useState<PwaDiagnosticsState>({
    checks: initialChecks,
    isLoading: true,
    overallStatus: 'loading',
    summary: 'Running diagnostics...',
  });

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setState(prev => {
        const checks = prev.checks.map(c =>
          c.name === 'beforeinstallprompt Event'
            ? { ...c, status: 'ok' as const, message: 'Event captured! App is installable.', detail: 'The browser has determined all criteria are met and emitted beforeinstallprompt.' }
            : c
        );
        return { ...prev, checks };
      });
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const runDiagnostics = useCallback(async () => {
    setState({
      checks: initialChecks,
      isLoading: true,
      overallStatus: 'loading',
      summary: 'Running diagnostics...',
    });

    const checks: PwaDiagnosticCheck[] = [];
    let overallStatus: PwaDiagnosticsState['overallStatus'] = 'unknown';

    // 1. Secure context
    const secure = isSecureContext();
    checks.push({
      name: 'HTTPS / Secure Context',
      status: secure ? 'ok' : 'fail',
      message: secure ? 'Secure context (HTTPS or localhost)' : 'NOT secure context',
      detail: secure
        ? undefined
        : 'PWA install requires HTTPS or localhost. You are on HTTP. Use HTTPS or ngrok/localtunnel for testing.',
    });

    // 2. Manifest fetchable
    const manifestData = await fetchManifest();
    checks.push({
      name: 'Manifest Fetchable',
      status: manifestData ? 'ok' : 'fail',
      message: manifestData ? `Manifest loaded from ${manifestData.url}` : 'Could not fetch manifest',
      detail: manifestData ? undefined : 'Check that <link rel="manifest" href="/manifest.webmanifest"> exists and the file is served.',
    });

    // 3. Manifest valid
    if (manifestData) {
      const m = manifestData.manifest;
      const hasName = !!m.name || !!m.short_name;
      const hasStartUrl = !!m.start_url;
      const hasDisplay = !!m.display;
      const valid = hasName && hasStartUrl && hasDisplay;
      checks.push({
        name: 'Manifest Valid',
        status: valid ? 'ok' : 'fail',
        message: valid
          ? `name="${m.name || m.short_name}", start_url="${m.start_url}", display="${m.display}"`
          : 'Manifest missing required fields',
        detail: valid
          ? undefined
          : `Missing: ${[!hasName && 'name/short_name', !hasStartUrl && 'start_url', !hasDisplay && 'display'].filter(Boolean).join(', ')}`,
      });

      // 4. Icons
      const icons = Array.isArray(m.icons) ? m.icons : [];
      const requiredSizes = ['192x192', '512x512'];
      const foundSizes = new Set<string>();
      const iconChecks: { src: string; accessible: boolean }[] = [];
      for (const icon of icons) {
        const sizes = icon.sizes?.split(' ') || [];
        for (const s of sizes) foundSizes.add(s);
        if (icon.src) {
          const accessible = await checkIconAccessible(icon.src);
          iconChecks.push({ src: icon.src, accessible });
        }
      }
      const missingSizes = requiredSizes.filter(s => !foundSizes.has(s));
      const anyIcon404 = iconChecks.some(c => !c.accessible);
      const iconStatus = missingSizes.length === 0 && !anyIcon404 ? 'ok' : missingSizes.length > 0 ? 'fail' : 'warn';
      checks.push({
        name: 'Manifest Icons',
        status: iconStatus,
        message: iconStatus === 'ok'
          ? `Found ${icons.length} icon(s). Required sizes present.`
          : iconStatus === 'fail'
            ? `Missing required sizes: ${missingSizes.join(', ')}`
            : `All sizes found but some icons returned 404`,
        detail: anyIcon404
          ? iconChecks.filter(c => !c.accessible).map(c => `404: ${c.src}`).join('\n')
          : `Required: 192x192 and 512x512 PNG. Found: ${Array.from(foundSizes).join(', ') || 'none'}.`,
      });
    } else {
      checks.push(
        { name: 'Manifest Valid', status: 'fail', message: 'Cannot validate — manifest not fetchable', detail: 'Fix manifest fetch first.' },
        { name: 'Manifest Icons', status: 'fail', message: 'Cannot check — manifest not fetchable', detail: 'Fix manifest fetch first.' }
      );
    }

    // 5. Service Worker registered
    let swRegistration: ServiceWorkerRegistration | null = null;
    let swActive = false;
    if ('serviceWorker' in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.ready;
        swActive = swRegistration.active?.state === 'activated';
      } catch {
        swRegistration = null;
      }
    }
    checks.push({
      name: 'Service Worker Registered',
      status: swRegistration ? 'ok' : 'fail',
      message: swRegistration ? `Scope: ${swRegistration.scope}` : 'No active service worker found',
      detail: swRegistration
        ? undefined
        : 'Check that VitePWA registerType is set and the SW is built. In dev, use preview mode.',
    });

    // 6. Service Worker active
    checks.push({
      name: 'Service Worker Active',
      status: swActive ? 'ok' : swRegistration ? 'warn' : 'fail',
      message: swActive
        ? 'Service Worker is activated'
        : swRegistration
          ? 'Service Worker exists but not yet activated'
          : 'No service worker',
      detail: swActive
        ? undefined
        : 'If waiting, reload the page or skip waiting in DevTools > Application > Service Workers.',
    });

    // 7. beforeinstallprompt
    const biPromptCheck: PwaDiagnosticCheck = {
      name: 'beforeinstallprompt Event',
      status: deferredPrompt ? 'ok' : 'info',
      message: deferredPrompt
        ? 'Event captured! Browser considers app installable.'
        : 'Event NOT received (yet)',
      detail: deferredPrompt
        ? undefined
        : 'This event is emitted by Chrome only when ALL criteria are met. If missing, one of the checks above is failing (most often: manifest icons 404, SW not active, or app already installed).',
    };
    checks.push(biPromptCheck);

    // 8. Display mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const standaloneIOS = ('standalone' in navigator) && (navigator as any).standalone === true;
    const isInstalled = standalone || standaloneIOS;
    checks.push({
      name: 'Display Mode',
      status: isInstalled ? 'info' : 'ok',
      message: isInstalled ? 'App is running in standalone mode (already installed)' : 'Running in browser tab',
      detail: isInstalled ? 'If already installed, beforeinstallprompt will NOT fire again for the same scope.' : undefined,
    });

    // Summary
    const failCount = checks.filter(c => c.status === 'fail').length;
    const warnCount = checks.filter(c => c.status === 'warn').length;
    if (isInstalled) {
      overallStatus = 'installed';
    } else if (deferredPrompt) {
      overallStatus = 'installable';
    } else if (failCount > 0) {
      overallStatus = 'not-installable';
    } else {
      overallStatus = 'unknown';
    }

    let summary: string;
    if (overallStatus === 'installed') {
      summary = 'App is already installed in standalone mode. Uninstall to see install prompt again.';
    } else if (overallStatus === 'installable') {
      summary = 'All criteria met! The Install button should appear (or use the InstallPwaButton).';
    } else if (failCount > 0) {
      summary = `${failCount} critical check(s) failed. Fix them before the app can be installed.`;
    } else if (warnCount > 0) {
      summary = 'No critical failures, but warnings present. Check them — they may prevent installation.';
    } else {
      summary = 'All checks passed but beforeinstallprompt not yet received. Try reloading or check if already installed.';
    }

    setState({ checks, isLoading: false, overallStatus, summary });
  }, [deferredPrompt]);

  // Run on mount
  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  return { ...state, runDiagnostics, deferredPrompt };
}
